import { AutocompleteProvider } from "../../../types/enums";
import { IMapInstance, LatLng } from "../../maps/map-provider.interface";
import { GeoAddress, IGeoSearchProvider } from "../geo-search-provider.interface";
import { ActiveBinding, BindingMode, HereAutocompleteItem, HereGeocodeItem, HereLookupResponse, HereRevGeocodeItem } from "./here-geo-search-provider.interface";

export class HereGeoSearchProvider implements IGeoSearchProvider {
    private readonly _apiKey: string;

    private _coordsBinding: ActiveBinding | null = null;
    private _addressBinding: ActiveBinding | null = null;

    private _abortGeocode: AbortController | null = null;

    constructor(apiKey: string) {
        this._apiKey = apiKey;
    }

    getGeoSearchProvider(): AutocompleteProvider {
        return AutocompleteProvider.HERE;
    }

    async bindAutocompleteAsync(
        autoCompleteElement: HTMLElement,
        mapInstance: IMapInstance,
        onSelected: (coords: LatLng) => void
    ): Promise<void> {
        const input = autoCompleteElement as HTMLInputElement;
        this._unbind("coords");

        const binding = this._createBinding("coords", input);
        binding.mapInstance = mapInstance;
        binding.onCoordsSelected = onSelected;

        this._coordsBinding = binding;
    }

    async bindAddressAutocompleteAsync(
        input: HTMLInputElement,
        options: { at?: LatLng; radiusMeters?: number },
        onSelected: (selected: GeoAddress) => void
    ): Promise<void> {
        this._unbind("address");

        const binding = this._createBinding("address", input);
        binding.options = options;
        binding.onAddressSelected = onSelected;

        this._addressBinding = binding;
    }

    async geocodeAsync(query: string): Promise<LatLng | null> {
        const q = query.trim();
        if (!q) return null;

        // Cancel any prior request
        if (this._abortGeocode)
            this._abortGeocode.abort();

        this._abortGeocode = new AbortController();

        try {
            const url = new URL("https://geocode.search.hereapi.com/v1/geocode");
            url.searchParams.set("apiKey", this._apiKey);
            url.searchParams.set("q", q);
            url.searchParams.set("limit", "1");

            const res = await fetch(url.toString(), { signal: this._abortGeocode.signal });
            if (!res.ok) return null;

            const json = await res.json();
            const item: HereGeocodeItem | undefined = (json.items?.[0] as HereGeocodeItem | undefined);
            const pos = item?.position;
            if (!pos) return null;

            return { lat: pos.lat, lng: pos.lng };
        }
        catch (err) {
            if (this._isAbortError(err))
                return null;

            console.error("HERE geocode failed", err);
            return null;
        }
    }

    async reverseGeocodeAsync(coords: LatLng): Promise<GeoAddress | null> {
        const url = new URL("https://revgeocode.search.hereapi.com/v1/revgeocode");
        url.searchParams.set("apiKey", this._apiKey);
        url.searchParams.set("at", `${coords.lat},${coords.lng}`);
        url.searchParams.set("limit", "1");

        const res = await fetch(url.toString());
        if (!res.ok) return null;

        const json = await res.json();
        const item: HereRevGeocodeItem | undefined = json.items?.[0];
        const a = item?.address;
        if (!a) return null;

        const street = a.street ?? undefined;

        return {
            coords,

            label: a.label ?? undefined,

            countryCode: a.countryCode ?? undefined,
            countryName: a.countryName ?? undefined,

            // HERE often returns the full name in `state`; sometimes also `stateCode`
            stateCode: (a.stateCode ?? undefined),
            stateName: (a.state ?? undefined),

            // Prefer city; fall back to district/county for regions that omit city
            city: (a.city ?? a.district ?? a.county ?? undefined),

            postalCode: a.postalCode ?? undefined,

            street,
            streetShortName: street, // HERE typically doesn't provide a distinct short street

            houseNumber: a.houseNumber ?? undefined,

            // Keep these for your "missing city" fallbacks in _parseGeoAddress
            district: (a.district ?? undefined),
            sublocality: (a.subdistrict ?? undefined)
        };
    }

    // Optional if you want cleanup when leaving the page
    dispose(): void {
        this._unbind("coords");
        this._unbind("address");

        if (this._abortGeocode) {
            this._abortGeocode.abort();
            this._abortGeocode = null;
        }
    }

    // ---------------------------------------------
    // Binding lifecycle
    // ---------------------------------------------
    private _unbind(mode: BindingMode): void {
        const b = mode === "coords" ? this._coordsBinding : this._addressBinding;
        if (!b) return;

        if (b.abortAutocomplete) b.abortAutocomplete.abort();
        if (b.abortLookup) b.abortLookup.abort();
        if (b.debounceTimer) window.clearTimeout(b.debounceTimer);

        b.input.removeEventListener("input", b.handleInput);
        b.input.removeEventListener("focus", b.handleFocus);
        b.input.removeEventListener("blur", b.handleBlur);

        document.removeEventListener("mousedown", b.handleDocDown);
        window.removeEventListener("resize", b.handleResize);

        b.dropdown.remove();

        if (mode === "coords")
            this._coordsBinding = null;
        else
            this._addressBinding = null;
    }

    private _createBinding(mode: BindingMode, input: HTMLInputElement): ActiveBinding {
        const { dropdown, parent } = this._createDropdown(input);

        const binding: ActiveBinding = {
            mode,
            input,
            dropdown,
            parent,

            abortAutocomplete: null,
            abortLookup: null,
            debounceTimer: null,

            handleInput: () => this._onInput(binding),
            handleFocus: () => this._onFocus(binding),
            handleBlur: () => this._onBlur(binding),
            handleDocDown: (e: MouseEvent) => this._onDocDown(binding, e),
            handleResize: () => this._onResize(binding)
        };

        this._positionDropdown(binding);

        input.addEventListener("input", binding.handleInput);
        input.addEventListener("focus", binding.handleFocus);
        input.addEventListener("blur", binding.handleBlur);
        document.addEventListener("mousedown", binding.handleDocDown);
        window.addEventListener("resize", binding.handleResize);

        return binding;
    }

    // ---------------------------------------------
    // UI helpers
    // ---------------------------------------------
    private _createDropdown(input: HTMLInputElement): { dropdown: HTMLDivElement; parent: HTMLElement } {
        const dropdown = document.createElement("div");
        dropdown.classList.add("geo-suggest-dropdown");
        dropdown.style.position = "absolute";
        dropdown.style.zIndex = "9999";
        dropdown.style.background = "white";
        dropdown.style.border = "1px solid #ccc";
        dropdown.style.borderRadius = "6px";
        dropdown.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
        dropdown.style.display = "none";
        dropdown.style.maxHeight = "260px";
        dropdown.style.overflowY = "auto";

        const parent = (input.parentElement as HTMLElement) ?? document.body;
        if (getComputedStyle(parent).position === "static")
            parent.style.position = "relative";
        parent.appendChild(dropdown);

        return { dropdown, parent };
    }

    private _positionDropdown(b: ActiveBinding): void {
        const r = b.input.getBoundingClientRect();
        const pr = b.parent.getBoundingClientRect();

        b.dropdown.style.left = `${r.left - pr.left}px`;
        b.dropdown.style.top = `${r.bottom - pr.top}px`;
        b.dropdown.style.width = `${r.width}px`;
    }

    private _hideDropdown(b: ActiveBinding): void {
        b.dropdown.style.display = "none";
        b.dropdown.innerHTML = "";
    }

    // ---------------------------------------------
    // Bias helpers
    // ---------------------------------------------
    private _getViewportBiasParams(b: ActiveBinding): { at?: string; inParam?: string } {
        const rect = b.mapInstance?.getBoundsRect?.();
        if (!rect) return {};

        const atLat = (rect.north + rect.south) / 2;
        const atLng = (rect.east + rect.west) / 2;

        return {
            at: `${atLat},${atLng}`,
            inParam: `bbox:${rect.west},${rect.south},${rect.east},${rect.north}`
        };
    }

    private _getOptionsBiasParams(b: ActiveBinding): { at?: string } {
        const at = b.options?.at;
        if (!at) return {};
        return { at: `${at.lat},${at.lng}` };
    }

    // ---------------------------------------------
    // Network helpers
    // ---------------------------------------------
    private async _fetchAutocompleteAsync(b: ActiveBinding, q: string): Promise<HereAutocompleteItem[]> {
        if (!this._apiKey) return [];

        if (b.abortAutocomplete)
            b.abortAutocomplete.abort();

        b.abortAutocomplete = new AbortController();

        try {
            const url = new URL("https://autocomplete.search.hereapi.com/v1/autocomplete");
            url.searchParams.set("apiKey", this._apiKey);
            url.searchParams.set("q", q);
            url.searchParams.set("limit", "6");

            // coords mode: bias to current map bounds
            // address mode: bias to options.at (if provided)
            const bias = b.mode === "coords" ? this._getViewportBiasParams(b) : this._getOptionsBiasParams(b);
            if (bias.at) url.searchParams.set("at", bias.at);
            // viewport bbox only makes sense when mapInstance exists
            if (b.mode === "coords") {
                const vb = this._getViewportBiasParams(b);
                if (vb.inParam) url.searchParams.set("in", vb.inParam);
            }

            const res = await fetch(url.toString(), { signal: b.abortAutocomplete.signal });
            if (!res.ok) return [];

            const json = await res.json();
            return (json.items ?? []) as HereAutocompleteItem[];
        }
        catch (err) {
            if (this._isAbortError(err))
                return [];

            console.error("HERE autocomplete failed", err);
            return [];
        }
    }

    private async _lookupCoordsByIdAsync(b: ActiveBinding, id: string): Promise<LatLng | null> {
        if (!this._apiKey) return null;

        if (b.abortLookup)
            b.abortLookup.abort();

        b.abortLookup = new AbortController();

        try {
            const url = new URL("https://lookup.search.hereapi.com/v1/lookup");
            url.searchParams.set("apiKey", this._apiKey);
            url.searchParams.set("id", id);

            const res = await fetch(url.toString(), { signal: b.abortLookup.signal });
            if (!res.ok) return null;

            const json = (await res.json()) as HereLookupResponse;
            const pos = json.position ?? json.access?.[0];
            if (!pos) return null;

            return { lat: pos.lat, lng: pos.lng };
        }
        catch (err) {
            if (this._isAbortError(err))
                return null;

            console.error("HERE lookup failed", err);
            return null;
        }
    }

    // ---------------------------------------------
    // Rendering + selection
    // ---------------------------------------------
    private _renderItems(b: ActiveBinding, items: HereAutocompleteItem[]): void {
        b.dropdown.innerHTML = "";

        if (items.length === 0) {
            this._hideDropdown(b);
            return;
        }

        this._positionDropdown(b);
        b.dropdown.style.display = "block";

        for (const it of items) {
            const id = it.id;
            const label = it.address?.label ?? it.title ?? "";
            if (!label) continue;

            const row = document.createElement("div");
            row.classList.add("geo-suggest-item");
            row.textContent = label;
            row.style.padding = "8px 10px";
            row.style.cursor = "pointer";
            row.style.userSelect = "none";

            row.addEventListener("mouseenter", () => (row.style.background = "#f3f4f6"));
            row.addEventListener("mouseleave", () => (row.style.background = "transparent"));

            // mousedown prevents blur from hiding before click lands
            row.addEventListener("mousedown", (e) => {
                e.preventDefault();
                this._handleItemSelectedAsync(b, label, id);
            });

            b.dropdown.appendChild(row);
        }
    }

    private async _handleItemSelectedAsync(b: ActiveBinding, label: string, id?: string): Promise<void> {
        b.input.value = label;
        this._hideDropdown(b);

        if (!id) return;

        const coords = await this._lookupCoordsByIdAsync(b, id);
        if (!coords) return;

        if (b.mode === "coords") {
            b.onCoordsSelected?.(coords);
            return;
        }

        // address mode: build full GeoAddress via reverse geocode
        const addr = await this.reverseGeocodeAsync(coords);
        if (!addr) {
            // minimal fallback if reverse fails
            b.onAddressSelected?.({
                coords,
                label
            });
            return;
        }

        // keep user-facing label if HERE label differs
        b.onAddressSelected?.({
            ...addr,
            label: addr.label ?? label
        });
    }

    // ---------------------------------------------
    // Debounce + handlers
    // ---------------------------------------------
    private _scheduleSearch(b: ActiveBinding, q: string): void {
        if (b.debounceTimer) window.clearTimeout(b.debounceTimer);

        b.debounceTimer = window.setTimeout(async () => {
            const trimmed = q.trim();
            if (trimmed.length < 2) {
                this._hideDropdown(b);
                return;
            }

            const items = await this._fetchAutocompleteAsync(b, trimmed);
            this._renderItems(b, items);
        }, 200);
    }

    private _onInput(b: ActiveBinding): void {
        this._scheduleSearch(b, b.input.value);
    }

    private _onFocus(b: ActiveBinding): void {
        this._positionDropdown(b);
        if (b.dropdown.innerHTML) b.dropdown.style.display = "block";
    }

    private _onBlur(b: ActiveBinding): void {
        window.setTimeout(() => this._hideDropdown(b), 150);
    }

    private _onDocDown(b: ActiveBinding, e: MouseEvent): void {
        const t = e.target as Node | null;
        if (t && (b.dropdown.contains(t) || b.input.contains(t))) return;
        this._hideDropdown(b);
    }

    private _onResize(b: ActiveBinding): void {
        if (b.dropdown.style.display !== "none") this._positionDropdown(b);
    }

    private _isAbortError(err: unknown): boolean {
        return err instanceof DOMException && err.name === "AbortError";
    }
}